import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="grain min-h-screen">
      <header className="app-header px-4 pb-4">
        <div className="mx-auto flex max-w-7xl items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="btn-quiet p-2"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="display text-xl font-semibold sm:text-2xl">Terms of Service</h1>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-12">
        <div className="panel space-y-6 p-8 sm:p-10">
          <div className="max-w-none">
            <h2 className="display mb-2 text-2xl font-semibold">Terms of Service</h2>
            <p className="mb-6 text-sm text-muted-foreground/70">Effective Date: July 29, 2026</p>

            <p className="mb-6 text-muted-foreground">
              Welcome to MovieMe! These Terms of Service (&quot;Terms&quot;) govern your use of the MovieMe
              website, iOS application, and related services (collectively, the &quot;Services&quot;). By
              accessing or using our Services, you agree to be bound by these Terms. If you do not
              agree, please do not use our Services.
            </p>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">1. Acceptance of Terms</h3>
              <p className="text-muted-foreground mb-4">By accessing or using MovieMe, you confirm that you:</p>
              <ul className="list-none space-y-2 mb-4">
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✅</span>
                  <span>Are at least 13 years old (or the minimum age required by your country's laws)</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✅</span>
                  <span>Have the legal capacity to agree to these Terms</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✅</span>
                  <span>Will comply with these Terms and all applicable laws and regulations</span>
                </li>
              </ul>
              <p className="text-muted-foreground">If you do not meet these requirements, you may not use the MovieMe Services.</p>
            </section>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">2. Description of Services</h3>
              <p className="text-muted-foreground">
                MovieMe is an AI-powered movie recommendation platform designed to help users discover
                movies based on mood, genre, time, and similar preferences. Recommendations are
                generated using AI and third-party movie metadata. They are suggestions only — we do
                not guarantee accuracy, suitability, or availability on any streaming platform, and
                MovieMe does not host or stream movies.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">3. User Accounts & Registration</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Account Creation – To access certain features, you may need to create an account by providing a valid email address or username.</li>
                <li>Account Security – You are responsible for keeping your login credentials secure. We are not liable for unauthorized access to your account.</li>
                <li>Account Termination – We reserve the right to suspend or terminate your account if you violate these Terms.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">3A. Free-tier usage limits</h3>
              <p className="text-muted-foreground">
                Free accounts currently receive up to 30 recommendation pulls per UTC calendar
                month. Initial results use one pull, and each &quot;load more&quot; batch uses another.
                Limits and reset timing are enforced by our servers and may change; we will update
                this page when they do.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">4. Acceptable Use</h3>
              <p className="text-muted-foreground mb-4">You agree NOT to:</p>
              <ul className="list-none space-y-2 mb-4">
                <li className="flex items-center gap-2">
                  <span className="text-red-600">🚫</span>
                  <span>Use MovieMe for any illegal or unauthorized purpose</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-red-600">🚫</span>
                  <span>Interfere with or disrupt our Services</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-red-600">🚫</span>
                  <span>Attempt to collect or distribute personal data of other users</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-red-600">🚫</span>
                  <span>Reverse-engineer, modify, or exploit our AI algorithms</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-red-600">🚫</span>
                  <span>Use MovieMe to spread misinformation or harmful content</span>
                </li>
              </ul>
              <p className="text-muted-foreground">
                We reserve the right to take action, including removing content, suspending accounts, or pursuing legal action, if you violate these terms.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">5. Privacy & Data Usage</h3>
              <p className="text-muted-foreground">
                Your use of MovieMe is also governed by our Privacy Policy, which explains how we collect, use, and protect your personal information. By using our Services, you agree to our data practices as described in the Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">6. Third-Party Services & Streaming Data</h3>
              <p className="text-muted-foreground">
                MovieMe does not host or provide access to streaming content. Instead, we aggregate movie information from third-party sources (e.g., TMDB, JustWatch, ReelGood). We are not responsible for the accuracy of streaming availability or external links.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">7. Limitation of Liability</h3>
              <div className="panel-raised space-y-4 p-4">
                <div className="flex items-start gap-2">
                  <span className="text-red-600 mt-1">🚨</span>
                  <div>
                    <p className="font-medium text-foreground">No Guarantees</p>
                    <p className="text-muted-foreground">MovieMe is provided "AS IS" without any warranties. We do not guarantee the accuracy of recommendations or third-party data.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-red-600 mt-1">🚨</span>
                  <div>
                    <p className="font-medium text-foreground">Limited Liability</p>
                    <p className="text-muted-foreground">To the maximum extent permitted by law, MovieMe shall not be liable for any indirect, incidental, or consequential damages arising from your use of the app.</p>
                  </div>
                </div>
              </div>
              <p className="text-muted-foreground mt-4">
                If you experience issues, please contact us before taking legal action.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="display mb-4 text-xl font-semibold">8. Changes to These Terms</h3>
              <p className="text-muted-foreground">
                We may update these Terms from time to time. Any changes will be posted on this page with a new Effective Date. Continued use of our Services after updates constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h3 className="display mb-4 text-xl font-semibold">9. Contact Information</h3>
              <p className="text-muted-foreground">
                For any questions about these Terms, contact us at:
              </p>
              <p className="text-muted-foreground mt-2">
                Email: info@moviemeapp.com
              </p>
              <p className="text-muted-foreground mt-4">
                Thank you for using MovieMe!
              </p>
            </section>
          </div>
        </div>
      </main>

      <footer className="mt-12 border-t border-white/[0.06] py-8">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-center text-muted-foreground text-sm">
            © 2026 MovieMe. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
